import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../config/axiosConfig";
import config from "../../config/api";

const BackendURL = config.backendUrl;
// ✅ Get All Hourly Rates
export const fetchSalesHourlyRates = createAsyncThunk(
    "salesHourlyRate/fetchAll",
    async (_, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.get(`${BackendURL}/saleshourlyrate`, {
                params: { _ts: Date.now() }, // Cache-busting for QA/proxy caches
                headers: {
                    "Cache-Control": "no-cache, no-store, max-age=0",
                    Pragma: "no-cache",
                    Expires: "0",
                },
            });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// ✅ Create Hourly Rates (Bulk)
export const createSalesHourlyRate = createAsyncThunk(
    "salesHourlyRate/create",
    async (payload, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.post(`${BackendURL}/saleshourlyrate/create`, payload);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// ✅ Update Hourly Rates (Bulk)
export const updateSalesHourlyRate = createAsyncThunk(
    "salesHourlyRate/update",
    async (payload, { rejectWithValue }) => {
        try {
            const res = await axiosInstance.put(`${BackendURL}/saleshourlyrate/update`, payload);
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// ✅ Delete Hourly Rate
export const deleteSalesHourlyRate = createAsyncThunk(
    "salesHourlyRate/delete",
    async (id, { rejectWithValue }) => {
        try {
            await axiosInstance.delete(`${BackendURL}/saleshourlyrate/delete/${id}`);
            return id;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

// -------------------- SLICE -------------------- //

const salesHourlyRateSlice = createSlice({
    name: "salesHourlyRate",
    initialState: {
        data: [],
        loading: false,
        error: null,
        successMessage: null,
    },
    reducers: {
        clearSalesHourlyRateState: (state) => {
            state.error = null;
            state.successMessage = null;
        },
    },
    extraReducers: (builder) => {
        builder

            // ---------- FETCH ----------
            .addCase(fetchSalesHourlyRates.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchSalesHourlyRates.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload;
            })
            .addCase(fetchSalesHourlyRates.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // ---------- CREATE ----------
            .addCase(createSalesHourlyRate.pending, (state) => {
                state.loading = true;
            })
            .addCase(createSalesHourlyRate.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message;
            })
            .addCase(createSalesHourlyRate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // ---------- UPDATE ----------
            .addCase(updateSalesHourlyRate.pending, (state) => {
                state.loading = true;
            })
            .addCase(updateSalesHourlyRate.fulfilled, (state, action) => {
                state.loading = false;
                state.successMessage = action.payload.message;

                // Update state data if returned
                if (action.payload.updated) {
                    action.payload.updated.forEach((updatedItem) => {
                        const index = state.data.findIndex(
                            (item) => item._id === updatedItem._id
                        );
                        if (index !== -1) {
                            state.data[index] = updatedItem;
                        }
                    });
                }
            })
            .addCase(updateSalesHourlyRate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // ---------- DELETE ----------
            .addCase(deleteSalesHourlyRate.pending, (state) => {
                state.loading = true;
            })
            .addCase(deleteSalesHourlyRate.fulfilled, (state, action) => {
                state.loading = false;
                state.data = state.data.filter(
                    (item) => item._id !== action.payload
                );
                state.successMessage = "Quotation deleted successfully";
            })
            .addCase(deleteSalesHourlyRate.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const { clearSalesHourlyRateState } = salesHourlyRateSlice.actions;

export default salesHourlyRateSlice.reducer;
